"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { COOKIE_CONSENT_UPDATED_EVENT } from "@/lib/tap-ref-capture.mjs";
import { clearNewsletterPersistentIdentifier } from "@/lib/newsletter-click-beacon.mjs";
import { isCookieConsentValid } from "@/lib/cookie-consent-validity.mjs";
import { usePathname } from "next/navigation";

/**
 * GDPR-compliant cookie consent with granular categories.
 *
 * Categories:
 * - necessary: always on (session, security) — no toggle
 * - analytics: GA4, Clarity — for site improvement
 * - marketing: TikTok, Meta, OpenAI (ChatGPT Ads), Tapfiliate — for ads and affiliate tracking
 *   NB OpenAI: its SDK has a server-side-toggled "automatic advanced
 *   matching" (form-field/PII harvesting) mode; kept inert via the CSP
 *   fail-closed guard in next.config.mjs (PR #1307, Opus B1/B2) until
 *   the operator switches it off in Ads Manager. Marketing consent here
 *   gates loading the SDK; it does NOT by itself gate that engine.
 *
 * Consent stored in localStorage as JSON: { analytics: bool, marketing: bool, timestamp }
 * Also exposes cookie_consent = "accepted" | "declined" | "partial" for backwards compat.
 */
function getConsent() {
  try {
    const raw = localStorage.getItem("cookie_consent_prefs");
    if (raw) return JSON.parse(raw);
  } catch {}
  // Legacy migration: old cookie_consent = "accepted" → all granted
  try {
    const legacy = localStorage.getItem("cookie_consent");
    if (legacy === "accepted") return { analytics: true, marketing: true };
    if (legacy === "declined") return { analytics: false, marketing: false };
  } catch {}
  return null;
}

// Genoemd geëxporteerd (was module-privaat) zodat andere consumers van
// `cookie_consent_prefs` -- zoals interest-beacon.test.js -- de ECHTE
// schrijver kunnen aanroepen i.p.v. de vorm handmatig te fixeren.
// Bot-bevinding PR #1438 r3: een handgebouwde fixture kan onopgemerkt
// afwijken van wat deze functie echt schrijft, waardoor een consumer
// fail-closed en STIL zou uitvallen. Gedrag ongewijzigd.
export function saveConsent(prefs) {
  try {
    const data = { ...prefs, timestamp: new Date().toISOString() };
    localStorage.setItem("cookie_consent_prefs", JSON.stringify(data));
    if (prefs.analytics && prefs.marketing) {
      localStorage.setItem("cookie_consent", "accepted");
    } else if (!prefs.analytics && !prefs.marketing) {
      localStorage.setItem("cookie_consent", "declined");
    } else {
      localStorage.setItem("cookie_consent", "partial");
    }
  } catch {
    // iOS private browsing / quota errors must not block banner dismissal.
  }
  if (!prefs.analytics && !prefs.marketing) {
    clearNewsletterPersistentIdentifier(localStorage);
  }
}

// Consent Mode v2: Google Ads needs all four fields for EEA visitors. The
// three ad fields follow `marketing`, also for a stored choice from before
// ad_user_data/ad_personalization existed (same cookie_consent_prefs shape,
// no re-prompt). Defaults (all four denied) live in app/layout.js.
// Strict booleans: a stored choice is only checked on its timestamp, so a
// tampered/corrupt value like marketing:"false" (truthy) must not grant.
function applyConsent(prefs) {
  try {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    const marketing = prefs.marketing === true;
    const ads = marketing ? "granted" : "denied";
    gtag("consent", "update", {
      analytics_storage: prefs.analytics === true ? "granted" : "denied",
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
    });
    if (marketing && typeof window.__loadTrackingPixels === "function") {
      window.__loadTrackingPixels();
    }
  } catch {
    // Pixel-loader failures must not block banner dismissal.
  }
}

function announceConsentUpdate() {
  try {
    window.dispatchEvent(new Event(COOKIE_CONSENT_UPDATED_EVENT));
  } catch {
    // A missing Event implementation must not block consent persistence.
  }
}

// Footer "Beheer cookies" (and any other surface) reopens the consent
// dialog by dispatching this event. AVG/GDPR: consent must stay
// adjustable after the initial choice (Dennis E2E QA 2026-06-10: the
// footer button silently did nothing once a choice was stored).
export const OPEN_COOKIE_PREFERENCES_EVENT = "favotrip:open-cookie-preferences";

// Compacte banner op Exclusive-routes: minder padding en kleinere letter,
// zodat de drie knoppen op 320 px op één rij passen.
const COMPACT_BANNER =
  "py-2 [&>div]:gap-2 [&_p]:text-xs [&_p]:leading-snug [&_button]:px-3 [&_button]:text-xs";

/**
 * /exclusive[/...] of /luxe[/...]: daar is de banner compacter (zelfde knoppen en keuzes).
 * Bewust alleen op het pad, voor IEDERE bezoeker van die paden: ook ingelogde
 * leden en ook met LUXE_PUBLIC_PAGES_LIVE uit (regie 26-10). Zelfde knoppen,
 * teksten en keuzes; alleen kleiner. Koppel dit niet aan de teaservlag of de sessie.
 */
export function isExclusiveBannerPath(pathname) {
  return typeof pathname === "string" && /^\/(?:exclusive|luxe)(?:\/|$)/.test(pathname);
}

export default function CookieConsent() {
  // Always start hidden on both server and client to avoid hydration
  // mismatch. The previous lazy-initializer pattern read localStorage
  // during render, undefined on SSR + defined on client, which produced
  // a server "banner visible" tree and a client "banner hidden" tree.
  // React resolved the mismatch by leaving the SSR banner DOM in place
  // without attaching event handlers, so taps on Accepteren did nothing
  // (Dennis report 2026-05-04). Decide visibility AFTER mount.
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const pathname = usePathname();
  const bannerRef = useRef(null);

  // NPS feedback page is privacy-friendly by design (no analytics, no
  // marketing pixels — spec docs/nps-pilot-spec.md §7.5). Showing a
  // cookie banner there is both legally unnecessary and a mobile-UX
  // hazard: the fixed banner overlaps the "Verstuur feedback" CTA on
  // small viewports and blocks the submit.
  // De ING-presentatie (/ing/presentation en daaronder) laadt geen enkele
  // tracker; toestemming vragen is daar niet nodig en de banner zou met
  // toetsenbordfocus achter de presentatie blijven staan.
  const onPresentationPage =
    pathname === "/ing/presentation" || pathname?.startsWith("/ing/presentation/");
  const onFeedbackPage = pathname?.startsWith("/feedback/") || onPresentationPage;
  // Favotrip Exclusive (advertentielanding): compacter, zodat hotel en ledenknop
  // op een kleine telefoon zichtbaar blijven (meting 26-10). Zelfde knoppen,
  // teksten en keuzes; nooit verborgen, uitgesteld of vooraf ingevuld.
  const compact = isExclusiveBannerPath(pathname);

  useEffect(() => {
    let prefs = null;
    try {
      prefs = getConsent();
    } catch {}
    if (!isCookieConsentValid(prefs)) {
      queueMicrotask(() => setVisible(true));
      return;
    }
    applyConsent(prefs);
  }, []);

  // Footer "Beheer cookies": reopen the dialog straight into the
  // preferences panel, with the toggles seeded from the stored choice
  // so the customer adjusts their actual consent instead of defaults.
  useEffect(() => {
    function reopen() {
      let prefs = null;
      try {
        prefs = getConsent();
      } catch {}
      if (prefs) {
        setAnalytics(prefs.analytics !== false);
        setMarketing(prefs.marketing !== false);
      }
      setShowPrefs(true);
      setVisible(true);
    }
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, reopen);
    return () =>
      window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, reopen);
  }, []);

  // Publish the banner height as a CSS variable so fixed bottom bars
  // (booking-wizard sticky price/CTA, mobile) can lift themselves above
  // the banner instead of being covered by it (E2E QA 2026-06-10: on a
  // 390px viewport the 155px banner fully hid the booking CTA until a
  // cookie choice was made).
  const bannerVisible = visible && !onFeedbackPage;
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    if (!bannerVisible) {
      root.style.setProperty("--ft-cookie-banner-h", "0px");
      return undefined;
    }
    const el = bannerRef.current;
    const publish = () => {
      // Hotfix 2026-06-11 (prod repro): on accept, a stale publish() plus a
      // trailing ResizeObserver callback re-wrote the old height (156px)
      // 6ms AFTER the cleanup wrote 0px, leaving the sticky wizard CTA
      // floating mid-screen on top of the travelers modal. Never publish a
      // height for an element that is no longer in the document.
      const h =
        el && el.isConnected ? Math.ceil(el.getBoundingClientRect().height) : 0;
      root.style.setProperty("--ft-cookie-banner-h", `${h}px`);
    };
    publish();
    let observer = null;
    if (typeof ResizeObserver !== "undefined" && el) {
      observer = new ResizeObserver(publish);
      observer.observe(el);
    }
    return () => {
      if (observer) observer.disconnect();
      root.style.setProperty("--ft-cookie-banner-h", "0px");
    };
  }, [bannerVisible, showPrefs]);

  function acceptAll() {
    const prefs = { analytics: true, marketing: true };
    saveConsent(prefs);
    applyConsent(prefs);
    announceConsentUpdate();
    setVisible(false);
  }

  function declineAll() {
    const prefs = { analytics: false, marketing: false };
    saveConsent(prefs);
    applyConsent(prefs);
    announceConsentUpdate();
    setVisible(false);
  }

  function savePreferences() {
    const prefs = { analytics, marketing };
    saveConsent(prefs);
    applyConsent(prefs);
    announceConsentUpdate();
    setVisible(false);
    setShowPrefs(false);
  }

  if (!bannerVisible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg"
      style={{
        bottom: "env(safe-area-inset-bottom, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className={`max-w-6xl mx-auto px-4 ${compact && !showPrefs ? COMPACT_BANNER : "py-3 sm:py-4"} sm:px-6`}>
        {!showPrefs ? (
          /* Main banner */
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
            <p className="text-xs sm:text-sm text-gray-700 leading-snug sm:leading-relaxed flex-1">
              {t("cookie.banner_intro")}{" "}
              <Link href="/privacy-policy" className="text-blue-600 underline">
                {t("cookie.privacy_link")}
              </Link>
              .
            </p>
            {/* Telefoon: de drie keuzes op één rij (geen wrap), Weigeren en
                Accepteren even groot en met dezelfde vulling. Vanaf sm: de
                oorspronkelijke opmaak. */}
            <div className="flex sm:flex-wrap gap-2 w-full sm:w-auto sm:flex-shrink-0">
              <button
                onClick={() => setShowPrefs(true)}
                className="flex-1 sm:flex-none px-2 sm:px-4 py-2 text-[13px] sm:text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {t("cookie.preferences_button")}
              </button>
              <button
                onClick={declineAll}
                className="flex-1 sm:flex-none px-2 sm:px-4 py-2 text-[13px] sm:text-sm font-medium sm:font-normal border border-[#2E2F86] bg-[#2E2F86] text-white hover:bg-[#252670] sm:border-gray-300 sm:bg-transparent sm:text-gray-700 sm:hover:bg-gray-50 rounded-md transition-colors"
              >
                {t("cookie.decline_button")}
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 sm:flex-none px-2 sm:px-5 py-2 text-[13px] sm:text-sm border border-[#2E2F86] sm:border-0 bg-[#2E2F86] text-white rounded-md hover:bg-[#252670] transition-colors font-medium"
              >
                {t("cookie.accept_button")}
              </button>
            </div>
          </div>
        ) : (
          /* Preferences panel */
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">
              {t("cookie.preferences_heading")}
            </h3>

            {/* Necessary - always on */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t("cookie.cat_necessary_label")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("cookie.cat_necessary_desc")}
                </p>
              </div>
              <div className="w-10 h-5 bg-[#2E2F86] rounded-full relative">
                <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t("cookie.cat_analytics_label")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("cookie.cat_analytics_desc")}
                </p>
              </div>
              <button
                aria-label={t("cookie.cat_analytics_label")}
                onClick={() => setAnalytics(!analytics)}
                className={`w-10 h-5 rounded-full relative transition-colors ${analytics ? "bg-[#2E2F86]" : "bg-gray-300"}`}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${analytics ? "right-0.5" : "left-0.5"}`}
                />
              </button>
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t("cookie.cat_marketing_label")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("cookie.cat_marketing_desc")}
                </p>
              </div>
              <button
                aria-label={t("cookie.cat_marketing_label")}
                onClick={() => setMarketing(!marketing)}
                className={`w-10 h-5 rounded-full relative transition-colors ${marketing ? "bg-[#2E2F86]" : "bg-gray-300"}`}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${marketing ? "right-0.5" : "left-0.5"}`}
                />
              </button>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => setShowPrefs(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {t("cookie.back_button")}
              </button>
              <button
                onClick={savePreferences}
                className="px-5 py-2 text-sm bg-[#2E2F86] text-white rounded-md hover:bg-[#252670] transition-colors font-medium"
              >
                {t("cookie.save_button")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
